/**
 * DocumentViewer — in-browser preview for DOCX and XLSX files.
 *
 * Feb 2026 (Slice B of the Office-in-browser plan). Read-only by design.
 * Each supported format is rendered client-side by a small dependency
 * so we never round-trip through a document server:
 *
 *   .docx           → docx-preview
 *   .xlsx / .xls    → SheetJS (community edition) → HTML tables per sheet
 *   .csv            → SheetJS → single-sheet HTML table
 *   .pptx           → unsupported client-side; download CTA only
 *
 * Contract with the parent (stable across the future OnlyOffice
 * upgrade — Slice A):
 *   props: {
 *     file:      { id, name, mime, size }
 *     projectId: string
 *     onClose:   () => void
 *     downloadUrl: string  (attachment-disposition URL — used by the
 *                           "Download to edit" CTA)
 *     previewUrl:  string  (inline-disposition URL — where the bytes
 *                           come from; auth token embedded)
 *   }
 *
 * When we swap to OnlyOffice we replace the render-body only — this
 * component's props / usage sites stay identical.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Download,
  Loader2,
  AlertTriangle,
  FileText,
  Table as TableIcon,
} from "lucide-react";

// ── Format detection ────────────────────────────────────────────────────

const EXT_RE = /\.([a-z0-9]+)$/i;

function extOf(name) {
  const m = (name || "").match(EXT_RE);
  return (m ? m[1] : "").toLowerCase();
}

/** Extension-based since Bunny mime metadata isn't always reliable. */
export function isViewableDocument(file) {
  const ext = extOf(file?.name);
  return ["docx", "xlsx", "xls", "csv"].includes(ext);
}

function kindOf(file) {
  const ext = extOf(file?.name);
  if (ext === "docx") return "docx";
  if (ext === "xlsx" || ext === "xls") return "xlsx";
  if (ext === "csv") return "csv";
  if (ext === "pptx") return "pptx";
  return "unknown";
}

// ── Component ───────────────────────────────────────────────────────────

export default function DocumentViewer({ file, onClose, downloadUrl, previewUrl }) {
  const kind = useMemo(() => kindOf(file), [file]);
  const [status, setStatus] = useState("loading"); // loading | ready | error | unsupported
  const [error, setError] = useState("");
  // For XLSX we render each sheet as its own HTML table and let the user
  // switch via tabs — mirrors the Excel-workbook mental model.
  const [sheets, setSheets] = useState([]); // [{name, html}]
  const [activeSheet, setActiveSheet] = useState(0);
  const docxHostRef = useRef(null);

  useEffect(() => {
    // Reset on every file swap so switching between files in the list
    // doesn't render stale content from the previous file.
    setStatus("loading");
    setError("");
    setSheets([]);
    setActiveSheet(0);

    if (!file || !previewUrl) return;

    let cancelled = false;
    (async () => {
      try {
        if (kind === "pptx" || kind === "unknown") {
          if (!cancelled) setStatus("unsupported");
          return;
        }
        const res = await fetch(previewUrl, { credentials: "include" });
        if (!res.ok) throw new Error(`Fetch failed (HTTP ${res.status})`);
        const buf = await res.arrayBuffer();
        if (cancelled) return;

        if (kind === "docx") {
          const mod = await import("docx-preview");
          if (cancelled) return;
          // The docx host div is always rendered (hidden until ready), so
          // the ref is populated by the time we get here — no RAF dance.
          if (!docxHostRef.current) {
            throw new Error("Viewer container not mounted");
          }
          docxHostRef.current.innerHTML = "";
          await mod.renderAsync(buf, docxHostRef.current, null, {
            className: "docx-preview-host",
            inWrapper: true,
            ignoreWidth: false,
            ignoreHeight: false,
            ignoreLastRenderedPageBreak: true,
          });
          if (!cancelled) setStatus("ready");
        } else if (kind === "xlsx" || kind === "csv") {
          const XLSX = await import("xlsx");
          if (cancelled) return;
          const wb = XLSX.read(buf, { type: "array", cellStyles: false });
          const rendered = wb.SheetNames.map((name) => {
            const sheet = wb.Sheets[name];
            const html = XLSX.utils.sheet_to_html(sheet, {
              editable: false,
              header: "",
              footer: "",
            });
            return { name, html };
          });
          if (!cancelled) {
            setSheets(rendered);
            setStatus("ready");
          }
        }
      } catch (e) {
        if (!cancelled) {
          setStatus("error");
          setError(String(e?.message || e) || "Failed to load document");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file?.id, previewUrl, kind]);

  const openInNewTab = () => {
    window.open(downloadUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={!!file} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        data-testid="document-viewer"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-[min(1200px,95vw)] w-full h-[92vh] p-0 gap-0 flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-zinc-800 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {kind === "docx" ? (
              <FileText className="w-4 h-4 text-blue-400 shrink-0" />
            ) : kind === "xlsx" || kind === "csv" ? (
              <TableIcon className="w-4 h-4 text-emerald-400 shrink-0" />
            ) : (
              <FileText className="w-4 h-4 text-zinc-500 shrink-0" />
            )}
            <DialogTitle
              className="text-sm font-medium text-zinc-100 truncate"
              data-testid="document-viewer-title"
            >
              {file?.name}
            </DialogTitle>
            <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 shrink-0 hidden sm:inline">
              · read-only preview
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              size="sm"
              variant="ghost"
              onClick={openInNewTab}
              data-testid="document-viewer-download"
              className="text-zinc-300 hover:text-amber-400"
              title="Download the original file to edit locally"
            >
              <Download className="w-3.5 h-3.5 mr-1.5" />
              Download to edit
            </Button>
            {/* Close affordance is provided by DialogContent's built-in
                top-right X (shadcn). We used to render a second X here,
                which rendered as a double close button in the header. */}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          {status === "loading" && (
            <div className="flex items-center justify-center flex-1 text-zinc-500 text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Rendering document…
            </div>
          )}

          {status === "unsupported" && (
            <div className="flex flex-col items-center justify-center flex-1 gap-3 text-center px-6">
              <AlertTriangle className="w-8 h-8 text-amber-400" />
              <div className="text-sm text-zinc-300">
                In-browser preview isn&apos;t available for this file type yet.
              </div>
              <div className="text-xs text-zinc-500 max-w-md">
                DOCX and XLSX files preview here directly. Other formats
                (PPTX, etc.) — download the file to view it locally.
              </div>
              <Button
                onClick={openInNewTab}
                className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
                data-testid="document-viewer-unsupported-download"
              >
                <Download className="w-4 h-4 mr-1.5" /> Download {file?.name}
              </Button>
            </div>
          )}

          {status === "error" && (
            <div className="flex flex-col items-center justify-center flex-1 gap-3 text-center px-6">
              <AlertTriangle className="w-8 h-8 text-rose-400" />
              <div className="text-sm text-zinc-300">
                Couldn&apos;t render this file in the browser.
              </div>
              {error && (
                <div className="text-xs text-zinc-500 max-w-md font-mono">
                  {error}
                </div>
              )}
              <Button
                onClick={openInNewTab}
                className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
                data-testid="document-viewer-error-download"
              >
                <Download className="w-4 h-4 mr-1.5" /> Download instead
              </Button>
            </div>
          )}

          {kind === "docx" && (
            <div
              className={`flex-1 overflow-auto bg-zinc-100 py-8 px-4 ${
                status === "ready" ? "" : "hidden"
              }`}
            >
              <div
                ref={docxHostRef}
                data-testid="document-viewer-docx"
                className="docx-viewer max-w-4xl mx-auto"
              />
            </div>
          )}

          {status === "ready" && (kind === "xlsx" || kind === "csv") && (
            <div className="flex-1 min-h-0 flex flex-col">
              {sheets.length > 1 && (
                <div
                  data-testid="document-viewer-sheet-tabs"
                  className="flex items-center gap-1 px-4 py-2 border-b border-zinc-800 overflow-x-auto shrink-0"
                >
                  {sheets.map((s, idx) => (
                    <button
                      key={`${s.name}-${idx}`}
                      type="button"
                      onClick={() => setActiveSheet(idx)}
                      data-testid={`document-viewer-sheet-tab-${idx}`}
                      className={`px-3 py-1 text-xs font-mono rounded-sm border transition-colors whitespace-nowrap ${
                        idx === activeSheet
                          ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10"
                          : "text-zinc-400 border-zinc-800 hover:text-zinc-100 hover:border-zinc-700"
                      }`}
                    >
                      {s.name}
                    </button>
                  ))}
                </div>
              )}
              <div
                className="flex-1 overflow-auto bg-zinc-100 text-zinc-900 xlsx-viewer"
                data-testid="document-viewer-xlsx"
                // SheetJS emits a full <table> — inject as HTML. Safe:
                // content originates from files uploaded by authenticated
                // users to their own project, and is rendered in an
                // isolated modal (no eval, sandbox implicit).
                dangerouslySetInnerHTML={{
                  __html: sheets[activeSheet]?.html || "",
                }}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
